import { NotImplementedError, ObjType, Validation } from "../../../types";
import { ObservableHandler } from "../../../handlers/observable";
import { filtersHasEmptyIdParam, parseIdFilter } from "../../../handlers/filter";
import { isServicePage } from "../../../handlers/page";
import axios from "axios";
import { Observable } from "rxjs";
import { validators } from "./validators";
import { ServiceInventoryMediaType } from "../../../models/inventories";
import { publisherOptimizationTemplateToService, ServicePublisherOptimizationTemplateStatus, serviceToPublisherOptimizationTemplate } from "../../../models/publisher/optimizationTemplate";
import { OptimizationTemplateFetchFailed, OptimizationTemplateSaveFailed } from "./errors";
import Uploads from "./uploads";
import Bundles from "./templateBundles";
import Channels from "./templateChannels";
import PublisherCaps from "./publisherCaps";
import Templates from "./templates";
import PublisherSettings from "../publisherSettings";
import { toQueryParams } from "./filters/params";
import { toFilterGroupObjects } from "./filters/objects";
class PublishersOptimizationTemplates extends ObservableHandler {
    constructor(sdk) {
        super(sdk, "publishers.optimizationTemplates", { atomize: true });
        this.toOptimizationTemplate = (template) => {
            return serviceToPublisherOptimizationTemplate(template);
        };
        this.url = `${this.sdk.urls.baseAPIUrl}/publisher-templates`;
        this.validators = validators(sdk);
        this.uploads = new Uploads(sdk);
        this.bundles = new Bundles(sdk);
        this.channels = new Channels(sdk);
        this.publisherCaps = new PublisherCaps(sdk);
        this.publisherSettings = new PublisherSettings(sdk);
        this.templates = new Templates(sdk);
    }
    /**
     * @param filters: filters to determine what optimization templates to get.
     * @return: an observable yielding the requested optimization templates.
     */
    findItems(filters, sort) {
        return new Observable((subscriber) => {
            const idFilter = parseIdFilter(filters);
            if (filtersHasEmptyIdParam(filters.where)) {
                subscriber.next([]);
                subscriber.complete();
            }
            else {
                const promise = idFilter.size === 1
                    ? this.getOptimizationTemplate(idFilter.values().next().value).then((template) => [
                        template
                    ])
                    : this.getOptimizationTemplates(filters, sort);
                promise
                    .then((templates) => {
                    subscriber.next(templates);
                    subscriber.complete();
                })
                    .catch((error) => {
                    subscriber.error(error);
                });
            }
        });
    }
    async getOptimizationTemplates(filters = {}, sort) {
        const params = toQueryParams(filters, sort);
        const pageSize = filters?.paging?.size || 10;
        return this.cache.promise(`${this.url}${params}`, () => axios
            .get(`${this.url}${params}`, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => {
            const templates = res.data?.map(this.toOptimizationTemplate) || [];
            let results = templates;
            if (isServicePage(res)) {
                results = {
                    page: {
                        count: res.paging_info?.count,
                        token: res.paging_info?.token,
                        size: pageSize
                    },
                    data: templates
                };
            }
            return results;
        })
            .catch((error) => {
            throw new OptimizationTemplateFetchFailed(error);
        }));
    }
    async getOptimizationTemplate(id) {
        const params = `?ids=["${id}"]`;
        return this.cache.promise(`${this.url}${params}`, () => axios
            .get(`${this.url}${params}`, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then(({ data: res }) => res?.data?.map((template) => this.toOptimizationTemplate(template))[0])
            .catch((error) => {
            throw new OptimizationTemplateFetchFailed(error);
        }));
    }
    saveItem(data, options) {
        const { skipValidation } = options || {};
        const promise = data.id ? this.update(data, skipValidation) : this.create(data, skipValidation);
        return new Observable((observer) => {
            promise
                .then(({ data: res }) => {
                const template = this.toOptimizationTemplate(Array.isArray(res.data) ? res.data[0] : res.data);
                this.cache.reconcile("save", template);
                observer.next(template);
                observer.complete();
            })
                .catch((error) => {
                if (error instanceof Validation) {
                    observer.error(new Error(error.values().next().value));
                    return;
                }
                observer.error(new OptimizationTemplateSaveFailed(error));
            });
        });
    }
    async create(templateBase, skipValidation = false) {
        const template = (await this.make(templateBase));
        if (!skipValidation) {
            const errors = await this.validate(template);
            if (errors.size) {
                throw errors;
            }
        }
        return axios.post(`${this.url}`, { data: [this.toService(template)] }, {
            headers: {
                "Content-Type": "application/json"
            }
        });
    }
    async update(template, skipValidation = false) {
        if (!skipValidation) {
            const errors = await this.validate(template);
            if (errors.size) {
                throw errors;
            }
        }
        return axios.patch(this.url, { data: [this.toService(template)] }, {
            headers: {
                "Content-Type": "application/json"
            }
        });
    }
    // The upload accepts this args so the BE can create the template with the correct metadata and return it.
    async upload(file, name, status = ServicePublisherOptimizationTemplateStatus.READY, mediaType = ServiceInventoryMediaType.VIDEO, templateId) {
        const template = await this.uploads.save({
            file,
            name,
            status,
            mediaType,
            templateId
        });
        return serviceToPublisherOptimizationTemplate(template);
    }
    parseFilter(filters) {
        return toFilterGroupObjects(filters || new Map());
    }
    make(defaults) {
        return new Promise((resolve, reject) => {
            this.sdk.cryptography
                .mintKey(ObjType.PUBLISHER_TEMPLATE)
                .then((id) => {
                const template = {
                    id,
                    name: "",
                    updatedAt: new Date(),
                    status: undefined,
                    publisherSettings: [],
                    ...defaults
                };
                resolve(template);
            })
                .catch((error) => {
                reject(error);
            });
        });
    }
    async deleteItem() {
        return new Promise((_, reject) => {
            reject(new NotImplementedError("delete"));
        });
    }
    toService(template) {
        return publisherOptimizationTemplateToService(template);
    }
}
export default PublishersOptimizationTemplates;
//# sourceMappingURL=index.js.map